import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";
import { specials } from "@/mocks/specials";

export default function Specials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = Number(searchParams.get("focus") ?? specials[0]?.id ?? 1);
  const active = specials.find((s) => s.id === focusId) ?? specials[0];
  const activeMovies = active.movieIds
    .map((mid) => movies.find((m) => m.id === mid))
    .filter((m): m is (typeof movies)[number] => Boolean(m));
  const relatedSpecials = specials.filter((s) => s.id !== active.id);

  usePageMeta({
    title: `${active.title} - 热门片单 - 555电影`,
    description: `${active.title}：${active.subtitle}。${active.description.slice(0, 50)}... 555电影为你精选优质片单。`,
    keywords: `${active.title},电影片单,热门专题,推荐电影,555电影`,
  });

  return (
    <div className="w-full animate-fadeIn">
      {/* Hero */}
      <div className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={active.cover}
          alt={`${active.title} 专题主图`}
          title={`${active.title} - 555电影`}
          className="absolute inset-0 h-full w-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/50"></div>
        <div className="relative z-10 mx-auto w-full max-w-screen-xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-4 text-background-300">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "热门片单专题" }]} />
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-500 px-3 py-1 text-xs font-semibold text-background-50">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-bookmark-3-fill text-xs"></i>
            </span>
            精选专题
          </span>
          <h1 className="mt-3 max-w-3xl font-heading text-3xl font-bold text-background-50 md:text-4xl">
            {active.title}
          </h1>
          <p className="mt-2 text-base text-accent-400 md:text-lg">{active.subtitle}</p>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            {active.description}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto flex w-full max-w-screen-xl gap-2 overflow-x-auto px-4 py-3 md:px-6">
          {specials.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSearchParams({ focus: `${s.id}` })}
              className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                s.id === active.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-600 hover:text-foreground-950"
              }`}
            >
              {s.title}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto w-full max-w-screen-xl px-4 py-10 md:px-6 md:py-12">
        {/* Reason */}
        <div className="mb-8 flex items-start gap-3 rounded-lg border-l-4 border-accent-500 bg-accent-50 p-4 md:p-5">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center text-accent-700">
            <i className="ri-lightbulb-flash-line text-xl"></i>
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-950">推荐理由</p>
            <p className="mt-1 text-sm leading-relaxed text-foreground-700">{active.reason}</p>
          </div>
        </div>

        {/* Movies */}
        <div className="mb-5 flex items-center gap-2">
          <span className="h-5 w-1 rounded-full bg-primary-500"></span>
          <h2 className="font-heading text-xl font-bold text-foreground-950">
            片单影片（{activeMovies.length}）
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {activeMovies.map((m) => (
            <MovieCard key={m.id} movie={m} />
          ))}
        </div>

        {/* Related specials */}
        <div className="mt-12">
          <div className="mb-5 flex items-center gap-2">
            <span className="h-5 w-1 rounded-full bg-secondary-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关专题</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {relatedSpecials.map((s) => (
              <Link
                key={s.id}
                to={`/specials?focus=${s.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={s.cover}
                    alt={`${s.title} 封面`}
                    title={`${s.title} - 555电影`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="line-clamp-1 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                    {s.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{s.subtitle}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </span>
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}